import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, InputAdornment } from "@mui/material";
import { Address1_2, Address2 } from "../../../assets/data/Constants";
import TkSelectForm from "../../../components/atoms/TkSelectForm";
import TkLabelText from "../../../components/atoms/TkLabelText";
import TkButton from "../../../components/atoms/TkButton";
import TkCorporateSearchModal from "../../../components/molecules/TkCorporateSearchModal";
import TkEmailInput from "../../../components/molecules/TkEmailInput";
import { GlobalContext } from "../../../context/globalContext";
import { TkGetCodeList } from "../../../utils/TkGetCode";
import TkPropertyMessageGet from "../../../utils/TkPropertyMessageGet";
import { httpAPI_CON001 } from "../api/request";
import "./_style.scss";
import { ClearIcon } from "@mui/x-date-pickers";

/**
 * 宛先２選択部品
 * @param props.defaultValue 初期値
 * @param props.onChange チェンジイベント
 * @return Address2Select
 */
const Address2Select = (props: any) => {
  const { defaultValue, disabled, onChange } = props;
  const [value, setValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (
      defaultValue?.addressKbn1 === Address1_2["既存の宛先を選択"] &&
      defaultValue?.addressKbn2
    ) {
      setValue(defaultValue.addressKbn2);
      onChange &&
        onChange({
          addressKbn2: defaultValue.addressKbn2,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = (e: any) => {
    onChange && onChange({ addressKbn2: e.target.value });
    const value = e.target.value;

    if (!value) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0044E"));
      onChange && onChange({});
      return;
    } else {
      setErrorMsg("");
    }
  };

  return (
    <Box data-testid="address2Select">
      <TkSelectForm
        errorMessage={errorMsg}
        selectPropsList={TkGetCodeList(Address2)}
        onChange={handleChange}
        wantToSetValue={value}
        fullWidth
        disabled={disabled}
      />
    </Box>
  );
};

/**
 * 法人検索部品
 * @param props.onChange チェンジイベント
 * @return CorporateSearch
 */
const CorporateSearch = (props: any) => {
  const { defaultValue, disabled, onChange } = props;

  const navigate = useNavigate();
  const { state, setState } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [corpList, setCorpList] = useState<any>([]);
  const [selectedItem, setSelectedItem] = useState<any>("");

  useEffect(() => {
    const item = `${defaultValue?.contractorNo ?? ""}${
      defaultValue?.corpName ?? ""
    } ${defaultValue?.email ?? ""}`;
    defaultValue && setSelectedItem(item);

    defaultValue && onChange && onChange(defaultValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleCallApi = async (data: any) => {
    // 契約者一覧参照API 呼び出し
    try {
      const res = await httpAPI_CON001(data);

      if (res.resultCd === "I0000") {
        setCorpList(res.contractorList);
        setOpen(true);

        setState({
          ...state,
          errorMsgId: "",
        });
      } else {
        setState({
          ...state,
          errorMsgId: res.resultCd,
        });
      }
    } catch {
      navigate("/error");
    }
  };

  const handleSave = (e: any) => {
    const item = `${e.contractorNo}:${e.corpName} ${e.contactEmail}`;
    setSelectedItem(item);

    onChange &&
      onChange({
        contractorNo: e.contractorNo,
        corpName: e.corpName,
        email: e.contactEmail,
      });
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedItem("");
    onChange && onChange({});
  };

  return (
    <Box className="SCRW130003" data-testid="corporateSearch">
      <Box className="corpSearch">
        <TkLabelText
          className="text"
          wantToSetValue={selectedItem}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClear}
                  edge="end"
                  sx={{ padding: 0.5, marginRight: 0.2 }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled
        />

        <TkButton disabled={disabled} onClick={() => setOpen(true)}>
          法人検索
        </TkButton>
      </Box>

      <TkCorporateSearchModal
        open={open}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        onChangeSearchData={handleCallApi}
        listData={corpList}
      />
    </Box>
  );
};

/**
 * メールアドレス入力部品
 * @param props.onChange
 * @return EmailInput
 */
const EmailInput = (props: any) => {
  const { defaultValue, disabled, onChange } = props;

  const [value, setValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (
      defaultValue?.addressKbn1 === Address1_2["個別に宛先を入力"] &&
      defaultValue?.addressKbn2
    ) {
      setValue(defaultValue.addressKbn2);
      onChange &&
        onChange({
          email: defaultValue.addressKbn2,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  const handleChange = (e: any) => {
    const value = e.target.value;

    if (!value) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0043E"));
      onChange && onChange({});
      return;
    } else if (value.length > 255) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0046E", "255"));
      onChange && onChange({});
      return;
    } else if (!value.match(/^[a-zA-Z0-9_.-]+@([a-zA-Z0-9-]*\.)+[a-z]{2,}$/)) {
      setErrorMsg(TkPropertyMessageGet("TKMSG_SCR0062E"));
      onChange && onChange({});
      return;
    } else {
      setErrorMsg("");
    }

    onChange && onChange({ email: value });
  };

  return (
    <Box data-testid="emailInput">
      <TkEmailInput
        helperText={errorMsg}
        wantToSetValue={value}
        disabled={disabled}
        onChange={handleChange}
      />
    </Box>
  );
};

const Address2Component = (props: any) => {
  const { address1, defaultValue, disabled, onChange } = props;

  switch (address1) {
    case Address1_2["個別に宛先を指定"]:
      return (
        <CorporateSearch
          defaultValue={defaultValue?.contractorDataList}
          disabled={disabled}
          onChange={onChange}
        />
      );

    case Address1_2["個別に宛先を入力"]:
      return (
        <EmailInput
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={onChange}
        />
      );

    default:
      return (
        <Address2Select
          defaultValue={defaultValue}
          disabled={disabled}
          onChange={onChange}
        />
      );
  }
};

export { Address2Select, CorporateSearch, EmailInput, Address2Component };
